/*
PURPOSE: Align/unalign button for user profile pages

USE CASES:
- Allow contributors to align with other contributors from user profile pages
- Provides visual feedback for alignment state (aligned vs not aligned)
- Handles alignment state changes with proper loading states

DOMAIN BOUNDARIES:
- Responsible for alignment action UI only, not data fetching
- Handles visual presentation and user interaction
- Integrates with alignment API functions

ASSUMPTIONS:
- Target user ID is provided as a prop
- Current user authentication is handled at app level
- API functions are available from api.ts
*/

import React, { useState, useEffect } from 'react';
import { 
  createAlignment, 
  removeAlignment, 
  checkAlignmentStatus,
  createArtistAlignment,
  removeArtistAlignment,
  checkArtistAlignmentStatus
} from '../../api/api';
import Spinner from '../Spinner/Spinner';
import ResonateIcon from '../ResonateIcon/ResonateIcon';

interface AlignButtonProps {
  targetUserId?: string;
  targetArtistId?: string;
  className?: string;
  disabled?: boolean;
  onAlignmentChange?: (isAligned: boolean) => void;
}

const AlignButton: React.FC<AlignButtonProps> = ({ 
  targetUserId, 
  targetArtistId,
  className = '', 
  disabled = false,
  onAlignmentChange 
}) => {
  const [isAligned, setIsAligned] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState<boolean>(true);

  // Determine if this is for a user or artist alignment
  const isArtistAlignment = !!targetArtistId;

  useEffect(() => {
    const checkStatus = async () => {
      try {
        setIsCheckingStatus(true);
        const response = isArtistAlignment 
          ? await checkArtistAlignmentStatus(targetArtistId!)
          : await checkAlignmentStatus(targetUserId!);
        setIsAligned(response.isAligned);
      } catch (error) {
        console.error('Error checking alignment status:', error);
        setIsAligned(false);
      } finally {
        setIsCheckingStatus(false);
      }
    };

    checkStatus();
  }, [targetUserId, targetArtistId, isArtistAlignment]);

  const handleAlignmentToggle = async () => {
    if (disabled || isLoading) return;

    try {
      setIsLoading(true);
      
      if (isAligned) {
        // Remove alignment
        if (isArtistAlignment) {
          await removeArtistAlignment(targetArtistId!);
        } else {
          await removeAlignment(targetUserId!);
        }
        setIsAligned(false);
        onAlignmentChange?.(false);
      } else {
        // Create alignment
        if (isArtistAlignment) {
          await createArtistAlignment(targetArtistId!);
        } else {
          await createAlignment(targetUserId!);
        }
        setIsAligned(true);
        onAlignmentChange?.(true);
      }
    } catch (error) {
      console.error('Error toggling alignment:', error);
      // Revert state on error
      setIsAligned(!isAligned);
    } finally {
      setIsLoading(false);
    }
  };

  // Validate props
  if ((!targetUserId && !targetArtistId) || (targetUserId && targetArtistId)) {
    console.error('AlignButton: Must provide either targetUserId OR targetArtistId, not both');
    return null;
  }

  // Show loading state while checking initial alignment status
  if (isCheckingStatus) {
    return (
      <button
        className={`
          text-sm font-bold uppercase px-4 py-2.5 rounded-full shadow-lg
          bg-gray-200 text-gray-500 cursor-not-allowed
          flex items-center justify-center
          ${className}
        `}
        disabled
      >
        <Spinner />
      </button>
    );
  }

  return (
    <div className="relative group">
      <button
        onClick={handleAlignmentToggle}
        className={`
          flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ease-in-out
          ${className}
          ${disabled || isLoading ? 
            'bg-gray-200 text-gray-500 cursor-not-allowed' : 
            isAligned ?
              'bg-white/90 text-coral-pink border border-coral-pink hover:bg-coral-pink/10' :
              'bg-white/90 text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-coral-pink hover:text-coral-pink'
          }
          text-sm font-medium shadow-sm hover:shadow-md
        `}
        disabled={disabled || isLoading}
        title={
          isAligned 
            ? "You're aligned - their content appears in your feed"
            : "Align to see all their content in your feed"
        }
      >
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <ResonateIcon 
              isResonated={isAligned} 
              isNested={false} 
              size={16}
            />
            <span className="pointer-events-none">
              {isAligned ? 'Aligned' : 'Align'}
            </span>
          </>
        )}
      </button>
      
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
        {isAligned 
          ? "You're aligned - their content appears in your feed" 
          : "Align to see all their content in your feed"
        }
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
      </div>
    </div>
  );
};

export default AlignButton;